import React, { useEffect, useState, useMemo } from 'react';
import { merchantApi } from '../../api/config';
import { bankList } from '../../utils/bankList';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUniversity, faPlus, faCheckCircle, faTrash,
  faEnvelope, faTimes, faEdit, faChevronDown, faChevronUp,
  faSpinner, faBan, faCircleCheck, faMoneyBillTransfer, faCopy,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
  UiModal,
  UiButton,
  UiToggle,
  StatusBadge,
} from '../../components/ui';
import type { StatCardDef, StatusDef } from '../../components/ui';
import type { MerchantTier } from '../../types';
import { WINDOW_MAX_SECONDS, WINDOW_DEFAULT_SECONDS, WINDOW_MAX_LIMIT, fmtWindow, getBankStyle } from '../../utils/bankLimit';

const INPUT_CLASS =
  'w-full px-3.5 py-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';
const LABEL_CLASS =
  'block text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1.5';
const FILTER_SELECT_CLASS =
  'w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';

const BANK_STATUS_MAP: Record<string, StatusDef> = {
  active:   { bg: 'bg-green-100',  text: 'text-green-700',  icon: faCircleCheck, label: 'Active'   },
  disabled: { bg: 'bg-gray-100',   text: 'text-gray-600',   icon: faBan,         label: 'Disabled' },
};

// ✅ ข้อมูล Header สำหรับ KTB Bulk File Export (เก็บใน banks.bulk_detail) — กรอกไม่บังคับ
const KTB_BULK_FIELDS: { key: string; label: string; placeholder?: string }[] = [
  { key: 'company_code',       label: 'Payer Abbreviation (ชื่อย่อบริษัท)', placeholder: 'เช่น SSKBA08105' },
  { key: 'company_id',         label: 'Company ID (Corporate ID)',          placeholder: 'เช่น SSKBA08105' },
  { key: 'tax_id',             label: 'เลขผู้เสียภาษี (Tax ID)',             placeholder: '13 หลัก' },
  { key: 'company_name_th',    label: 'ชื่อบริษัท (ไทย)' },
  { key: 'company_name_en',    label: 'ชื่อบริษัท (อังกฤษ)' },
];

const emptyBulkDetail = (): Record<string, string> => ({
  company_code: '', company_id: '', tax_id: '',
  company_name_th: '', company_name_en: '',
});

// ✅ กล่องเมล Mailkub สำหรับรับ OTP ของ KTB (เก็บใน banks.credential.otp_mailbox)
// api_key / webhook_secret เป็น write-only — ฟอร์มแก้ไขเว้นว่าง = คงค่าเดิม
const emptyOtpMailbox = () => ({
  email: '', api_key: '', webhook_secret: '', mailbox_id: '', webhook_url: '',
});

const copyToClipboard = async (text: string) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand('copy'); // คืน false (ไม่ throw) เมื่อ browser บล็อก
      document.body.removeChild(el);
      if (!ok) throw new Error('execCommand copy failed');
    }
    toast.success('คัดลอกแล้ว', { autoClose: 1000 });
  } catch {
    toast.error('คัดลอกไม่สำเร็จ');
  }
};

// ค่าเริ่มต้นฟอร์มเพิ่มธนาคาร — ใช้ทั้งตอน mount, หลังบันทึกสำเร็จ และตอนปิด modal (กัน secret ค้างไปโผล่ในธนาคารถัดไป)
const emptyBankForm = () => ({
  name: '',
  account_number: '',
  promptpay_id: '',
  bank_code: '',
  fetch_type: 'api',
  xaccesstoken: '',
  xhmac: '',
  email_address: '',
  email_password: '',
  imap_host: 'outlook.office365.com',
  imap_port: 993,
  email_subject_filter: 'SCB Business Alert',
  merchant_ids: [] as string[],
  all_merchants: false,
  is_withdraw: false,
  daily_transaction_limit: 10000,
  daily_deposit_limit: 0,
  // เพดานช่วงเวลา (N รายการ / S วินาที — ซ้อนกับรายวัน) · 0 = ปิด · default 300 วินาที (5 นาที)
  limit_window_seconds: 300,
  window_transaction_limit: 0,
  credential: {
    companyId: '',
    userId: '',
    password: '',
    otp_mailbox: emptyOtpMailbox()
  },
  bulk_detail: emptyBulkDetail()
});

// ⭐ เพดานช่วงเวลา — tenant ที่ยังไม่ migrate (window_limit_supported=false) ห้ามส่ง 2 key นี้ (backend จะ PGRST204)
const stripWindowFields = <T extends { limit_window_seconds: number; window_transaction_limit: number }>(form: T, supported: boolean) => {
  if (supported) return form;
  const { limit_window_seconds, window_transaction_limit, ...rest } = form;
  return rest;
};

// ⭐ หมวดเพดานต่อบัญชี (ใช้ทั้ง create/edit modal) — 2 การ์ด แต่ละหมวดมีสวิตช์เปิด/ปิดเอง (ปิด = เก็บ 0)
//    ทุกเพดานที่เปิดต้องผ่านพร้อมกัน (AND) ถึงจะอยู่ใน pool ที่ระบบสุ่มตอนสร้าง order
interface BankLimitValues {
  daily_transaction_limit: number;
  daily_deposit_limit: number;
  limit_window_seconds: number;
  window_transaction_limit: number;
}
interface BankLimitSectionProps {
  values: BankLimitValues;
  onChange: (patch: Partial<BankLimitValues>) => void;
  windowSupported: boolean;
}

const parseCount = (raw: string, fallback = 0) => (raw === '' ? fallback : Math.max(0, parseInt(raw, 10) || 0));

const BankLimitSection: React.FC<BankLimitSectionProps> = ({ values, onChange, windowSupported }) => {
  const dailyOn = values.daily_transaction_limit > 0 || values.daily_deposit_limit > 0;
  const windowOn = values.window_transaction_limit > 0;
  return (
    <div className="col-span-2 space-y-3">
      <div className="bg-gray-50 dark:bg-slate-700/40 rounded-xl p-4 border border-gray-100 dark:border-slate-600">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div>
            <h4 className="font-semibold text-gray-700 dark:text-slate-200 text-sm">เพดานรายวัน</h4>
            <p className="text-xs text-gray-400 dark:text-slate-500">นับใหม่ทุกวัน (เวลาไทย) • ปิด = ไม่จำกัด</p>
          </div>
          <UiToggle
            checked={dailyOn}
            ariaLabel="เปิดเพดานรายวัน"
            onChange={(on) => onChange(on
              ? { daily_transaction_limit: 10000, daily_deposit_limit: 0 }
              : { daily_transaction_limit: 0, daily_deposit_limit: 0 })}
          />
        </div>
        {dailyOn && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={LABEL_CLASS}>จำนวน Transaction สูงสุดต่อวัน</label>
              <input
                type="number"
                min={0}
                value={values.daily_transaction_limit}
                onChange={(e) => onChange({ daily_transaction_limit: parseCount(e.target.value) })}
                className={INPUT_CLASS}
              />
              <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">ค่าเริ่มต้น 10,000 transaction/วัน • ใส่ 0 = ไม่จำกัด</p>
            </div>
            <div>
              <label className={LABEL_CLASS}>เพดานยอดฝากต่อวัน (บาท)</label>
              <input
                type="number"
                min={0}
                value={values.daily_deposit_limit}
                onChange={(e) => onChange({ daily_deposit_limit: e.target.value === '' ? 0 : parseFloat(e.target.value) })}
                className={INPUT_CLASS}
              />
              <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">ยอดฝาก (paid) รวมต่อวันของธนาคารนี้ • ใส่ 0 = ไม่จำกัด</p>
            </div>
          </div>
        )}
      </div>

      {windowSupported && (
        <div className="bg-blue-50/60 dark:bg-blue-900/10 rounded-xl p-4 border border-blue-100 dark:border-blue-900/40">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <h4 className="font-semibold text-gray-700 dark:text-slate-200 text-sm">เพดานช่วงเวลา (วินาที)</h4>
              <p className="text-xs text-gray-400 dark:text-slate-500">
                นับ order ย้อนหลัง S วินาทีล่าสุด ณ ตอนสร้าง (รวมใบที่ยังรอชำระ) ถ้าถึง N รายการ บัญชีนี้จะหยุดรับชั่วคราว • ใช้ร่วมกับเพดานรายวัน ต้องผ่านทุกเพดานที่เปิด
              </p>
            </div>
            <UiToggle
              checked={windowOn}
              ariaLabel="เปิดเพดานช่วงเวลา"
              onChange={(on) => onChange({ window_transaction_limit: on ? 10 : 0 })}
            />
          </div>
          {windowOn && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={LABEL_CLASS}>ช่วงเวลา (วินาที)</label>
                <input
                  type="number"
                  min={1}
                  max={WINDOW_MAX_SECONDS}
                  value={values.limit_window_seconds}
                  onChange={(e) => onChange({
                    limit_window_seconds: Math.min(WINDOW_MAX_SECONDS, Math.max(1, parseCount(e.target.value, WINDOW_DEFAULT_SECONDS) || WINDOW_DEFAULT_SECONDS)),
                  })}
                  className={INPUT_CLASS}
                />
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                  1–3,600 วินาที (60 = 1 นาที, สูงสุด 3,600 = 60 นาที) • ตอนนี้ = {fmtWindow(values.limit_window_seconds)}
                </p>
              </div>
              <div>
                <label className={LABEL_CLASS}>จำนวนรายการสูงสุดในช่วง</label>
                <input
                  type="number"
                  min={0}
                  max={WINDOW_MAX_LIMIT}
                  value={values.window_transaction_limit}
                  onChange={(e) => onChange({ window_transaction_limit: Math.min(WINDOW_MAX_LIMIT, parseCount(e.target.value)) })}
                  className={INPUT_CLASS}
                />
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">ใส่ 0 = ปิดเพดานนี้ • สูงสุด 10,000</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ตัด whitespace อีเมลกล่อง Mailkub ก่อนส่ง (backend เทียบแบบ trim แล้ว — กันลงทะเบียนซ้ำเพราะเว้นวรรคท้าย)
const withTrimmedMailbox = <T extends { credential: { otp_mailbox: { email: string } } }>(form: T): T => ({
  ...form,
  credential: {
    ...form.credential,
    otp_mailbox: { ...form.credential.otp_mailbox, email: form.credential.otp_mailbox.email.trim() }
  }
});

// ⭐ ระดับร้านค้าที่ใช้ธนาคารนี้ได้ — state แยกจาก form ที่ spread ลง payload (ADR-0008)
// ไม่มี toggle 'all' — payload เป็น string[] เสมอ; [] = ทุกระดับใช้ได้
interface TierSelectorProps {
  tiers: MerchantTier[];
  selected: string[];
  onToggle: (id: string) => void;
}

const TierSelector: React.FC<TierSelectorProps> = ({ tiers, selected, onToggle }) => (
  <div className="bg-indigo-50/60 dark:bg-indigo-900/10 rounded-xl p-4 border border-indigo-100 dark:border-indigo-900/40">
    <h4 className="font-semibold text-gray-700 dark:text-slate-200 text-sm mb-3">
      ระดับร้านค้าที่ใช้ธนาคารนี้ได้ <span className="font-normal text-gray-400 dark:text-slate-500">({selected.length} ที่เลือก)</span>
    </h4>
    {tiers.length === 0 ? (
      <p className="text-xs text-gray-400 dark:text-slate-500">ยังไม่มีระดับร้านค้าในระบบ — สร้างได้ที่เมนู "ระดับร้านค้า"</p>
    ) : (
      <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
        {tiers.map((tier) => (
          <label
            key={tier.id}
            className={`flex items-center gap-2 cursor-pointer p-2 rounded-lg border transition-colors
              ${selected.includes(tier.id)
                ? 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-300 dark:border-indigo-700'
                : 'bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'}`}
          >
            <input
              type="checkbox"
              checked={selected.includes(tier.id)}
              onChange={() => onToggle(tier.id)}
              className="w-4 h-4 rounded focus:ring-gray-300 dark:focus:ring-slate-500"
            />
            <span
              className="w-2.5 h-2.5 rounded-full border border-black/10 dark:border-white/10 flex-shrink-0"
              style={{ background: tier.color || '#9ca3af' }}
            />
            <span className="text-sm text-gray-700 dark:text-slate-200 truncate">
              {tier.name}
              <span className="text-[10px] text-gray-400 dark:text-slate-500 ml-1">rank {tier.rank}</span>
              {!tier.is_active && <span className="text-[10px] text-red-400 ml-1">(ปิดใช้งาน)</span>}
            </span>
          </label>
        ))}
      </div>
    )}
    <p className="text-xs text-gray-400 dark:text-slate-500 mt-3">ไม่เลือก = ทุกระดับใช้ได้ (ร้านต้องถูกผูกกับธนาคารนี้ในรายการด้านบนด้วย)</p>
  </div>
);

const BanksManagementPage: React.FC = () => {
  const [banks, setBanks] = useState<any[]>([]);
  const [merchants, setMerchants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [expandedMerchants, setExpandedMerchants] = useState<Set<string>>(new Set());
  const [editingBank, setEditingBank] = useState<any>(null);
  const [bankBalances, setBankBalances] = useState<Record<string, { balance: number | null; loading: boolean; error?: string }>>({});
  const [filterBank, setFilterBank] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [formData, setFormData] = useState(emptyBankForm());
  const [editFormData, setEditFormData] = useState(emptyBankForm());
  // ⭐ ระดับร้านค้า — tiersSupported=false บน tenant ที่ยังไม่ migrate → ซ่อน block + ไม่ส่ง allowed_tier_ids
  const [tiers, setTiers] = useState<MerchantTier[]>([]);
  const [tiersSupported, setTiersSupported] = useState(false);
  // ⭐ เพดานช่วงเวลา — windowSupported=false บน tenant ที่ยังไม่ migrate (จาก GET /admin/banks) → ซ่อนการ์ด/คอลัมน์ + ไม่ส่ง key
  const [windowSupported, setWindowSupported] = useState(false);
  const [createTierIds, setCreateTierIds] = useState<string[]>([]);
  const [editTierIds, setEditTierIds] = useState<string[]>([]);
  const [depositAdminBankId, setDepositAdminBankId] = useState('');

  useEffect(() => {
    loadBanks();
    loadMerchants();
    loadTiers();
    loadDepositAdminBankId();
  }, []);

  const loadTiers = async () => {
    try {
      const response = await merchantApi.get('/admin/merchant-tiers');
      const supported = response.data?.supported === true;
      setTiersSupported(supported);
      setTiers(supported ? (response.data.tiers || []) : []);
    } catch (error: any) {
      setTiersSupported(false);
      toast.error(error.response?.data?.message || 'โหลดระดับร้านค้าไม่สำเร็จ');
    }
  };

  // บัญชี fallback ของ P2P — ถ้าจำกัดระดับ ร้าน P2P ระดับอื่นที่ไม่มีบัญชีผูกจะ 503
  const loadDepositAdminBankId = async () => {
    try {
      const response = await merchantApi.get('/admin/settings');
      const row = (response.data?.data || []).find((s: any) => s.setting_key === 'deposit_admin_bank_id');
      setDepositAdminBankId(row?.setting_value ?? '');
    } catch (error: any) {
      // ค่านี้ใช้แค่โชว์คำเตือน P2P ใน modal — โหลดไม่ได้ก็ยังจัดการธนาคารได้ แต่ต้องบอกให้รู้ว่าคำเตือนจะไม่ขึ้น
      toast.warn(error.response?.data?.message || 'โหลดค่า deposit_admin_bank_id ไม่สำเร็จ — คำเตือนบัญชี fallback P2P จะไม่แสดง');
    }
  };

  const tiersById = useMemo(() => new Map(tiers.map((t) => [t.id, t])), [tiers]);

  const toggleCreateTier = (id: string) =>
    setCreateTierIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const toggleEditTier = (id: string) =>
    setEditTierIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const loadBanks = async () => {
    try {
      const response = await merchantApi.get('/admin/banks?with_merchants=true');
      setBanks(response.data.data);
      setWindowSupported(response.data?.window_limit_supported === true);
    } catch (error: any) {
      toast.error('ไม่สามารถโหลดข้อมูลได้');
    } finally {
      setLoading(false);
    }
  };

  const loadMerchants = async () => {
    try {
      const response = await merchantApi.get('/merchants');
      setMerchants(response.data.data);
    } catch (error) {
      console.error('Error loading merchants');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await merchantApi.post('/admin/banks/create', {
        ...stripWindowFields(withTrimmedMailbox(formData), windowSupported),
        merchant_ids: formData.all_merchants ? 'all' : formData.merchant_ids,
        // ส่ง allowed_tier_ids เฉพาะ tenant ที่รองรับ (ส่งไปบน tenant ที่ไม่รองรับ = 400)
        ...(tiersSupported ? { allowed_tier_ids: createTierIds } : {})
      });
      toast.success('เพิ่มธนาคารสำเร็จ!');
      const mailboxSync = response.data?.mailbox_sync;
      if (mailboxSync?.ok === false) {
        toast.warning(mailboxSync.message || 'บันทึกแล้ว แต่ลงทะเบียนกล่องเมล OTP ไม่สำเร็จ');
      } else if (mailboxSync?.ok === true && mailboxSync.webhook_url) {
        toast.info('ลงทะเบียนกล่องเมล OTP แล้ว — กด "แก้ไข" ที่ธนาคารนี้เพื่อคัดลอก Webhook URL ไปใส่ใน Mailkub');
      }
      setShowCreateModal(false);
      setFormData(emptyBankForm());
      setCreateTierIds([]);
      loadBanks();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    }
  };

  const handleMerchantToggle = (merchantId: string) => {
    setFormData(prev => ({
      ...prev,
      merchant_ids: prev.merchant_ids.includes(merchantId)
        ? prev.merchant_ids.filter(id => id !== merchantId)
        : [...prev.merchant_ids, merchantId]
    }));
  };

  const handleEditMerchantToggle = (merchantId: string) => {
    setEditFormData(prev => ({
      ...prev,
      merchant_ids: prev.merchant_ids.includes(merchantId)
        ? prev.merchant_ids.filter(id => id !== merchantId)
        : [...prev.merchant_ids, merchantId]
    }));
  };

  const handleOpenEditModal = (bank: any) => {
    setEditingBank(bank);
    setEditFormData({
      name: bank.name || '',
      account_number: bank.account_number || '',
      promptpay_id: bank.promptpay_id || '',
      bank_code: bank.bank_code || '',
      fetch_type: bank.fetch_type || 'api',
      xaccesstoken: bank.xaccesstoken || '',
      xhmac: bank.xhmac || '',
      email_address: bank.email_address || '',
      email_password: '', // ไม่ส่ง password กลับมา
      imap_host: bank.imap_host || 'outlook.office365.com',
      imap_port: bank.imap_port || 993,
      email_subject_filter: bank.email_subject_filter || 'SCB Business Alert',
      merchant_ids: bank.merchants?.map((m: any) => m.id) || [],
      all_merchants: false,
      is_withdraw: bank.is_withdraw || false,
      daily_transaction_limit: bank.daily_transaction_limit ?? 10000,
      daily_deposit_limit: bank.daily_deposit_limit ?? 0,
      limit_window_seconds: bank.limit_window_seconds ?? WINDOW_DEFAULT_SECONDS,
      window_transaction_limit: bank.window_transaction_limit ?? 0,
      credential: {
        companyId: bank.credential?.companyId || '',
        userId: bank.credential?.userId || '',
        password: bank.credential?.password || '',
        otp_mailbox: {
          email: bank.credential?.otp_mailbox?.email || '',
          api_key: '',        // write-only — เว้นว่าง = คงค่าเดิม
          webhook_secret: '', // write-only — เว้นว่าง = คงค่าเดิม
          mailbox_id: bank.credential?.otp_mailbox?.mailbox_id || '',
          webhook_url: bank.credential?.otp_mailbox?.webhook_url || '',
        }
      },
      bulk_detail: { ...emptyBulkDetail(), ...(bank.bulk_detail || {}) }
    });
    setEditTierIds(Array.isArray(bank.allowed_tier_ids) ? [...bank.allowed_tier_ids] : []);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingBank) return;
    
    try {
      const response = await merchantApi.put(`/admin/banks/${editingBank.id}`, {
        ...stripWindowFields(withTrimmedMailbox(editFormData), windowSupported),
        merchant_ids: editFormData.all_merchants ? 'all' : editFormData.merchant_ids,
        ...(tiersSupported ? { allowed_tier_ids: editTierIds } : {})
      });
      toast.success('อัพเดทธนาคารสำเร็จ!');
      const mailboxSync = response.data?.mailbox_sync;
      if (mailboxSync?.ok === false) {
        toast.warning(mailboxSync.message || 'บันทึกแล้ว แต่ลงทะเบียนกล่องเมล OTP ไม่สำเร็จ');
      }
      setShowEditModal(false);
      setEditingBank(null);
      loadBanks();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'เกิดข้อผิดพลาด');
    }
  };

  const handleDeleteBank = async (bankId: string) => {
    if (!window.confirm('คุณต้องการลบธนาคารนี้?')) return;
    try {
      await merchantApi.delete(`/admin/banks/${bankId}`);
      toast.success('ลบธนาคารสำเร็จ');
      loadBanks();
    } catch (error: any) {
      toast.error('เกิดข้อผิดพลาด');
    }
  };

  const handleToggleStatus = async (bankId: string, isActive: boolean) => {
    try {
      await merchantApi.put(`/admin/banks/${bankId}/toggle-status`, {
        is_active: isActive
      });
      toast.success(isActive ? 'เปิดใช้งานธนาคารแล้ว' : 'ปิดใช้งานธนาคารแล้ว');
      loadBanks();
    } catch (error: any) {
      toast.error('เกิดข้อผิดพลาด');
    }
  };

  const fetchBankBalance = async (bankId: string) => {
    setBankBalances(prev => ({ ...prev, [bankId]: { balance: null, loading: true } }));
    try {
      const response = await merchantApi.get(`/admin/banks/${bankId}/balance`);
      const data = response.data.data;
      setBankBalances(prev => ({ ...prev, [bankId]: { balance: data.balance, loading: false } }));
    } catch (error: any) {
      const msg = error.response?.data?.message || 'ดึงยอดไม่สำเร็จ';
      setBankBalances(prev => ({ ...prev, [bankId]: { balance: null, loading: false, error: msg } }));
      toast.error(msg);
    }
  };

  const filteredBanks = useMemo(() => {
    return banks
      .filter((bank) => {
        if (filterBank && bank.bank_code !== filterBank) return false;
        if (filterType === 'withdraw' && !bank.is_withdraw) return false;
        if (filterType === 'deposit' && bank.is_withdraw) return false;
        if (filterStatus === 'active' && !bank.is_active) return false;
        if (filterStatus === 'disabled' && bank.is_active) return false;
        if (filterStatus === 'suspended' && (!bank.suspensions || bank.suspensions.length === 0)) return false;
        return true;
      })
      .sort((a, b) => {
        const aHasSuspension = a.suspensions && a.suspensions.length > 0;
        const bHasSuspension = b.suspensions && b.suspensions.length > 0;
        const aScore = (a.is_active ? 0 : 2) + (aHasSuspension ? 1 : 0);
        const bScore = (b.is_active ? 0 : 2) + (bHasSuspension ? 1 : 0);
        return aScore - bScore;
      });
  }, [banks, filterBank, filterType, filterStatus]);

  const activeCount = banks.filter(b => b.is_active).length;
  const withdrawCount = banks.filter(b => b.is_withdraw).length;
  const suspendedCount = banks.filter(b => b.suspensions?.length > 0).length;

  const statCards: StatCardDef[] = [
    {
      icon: faUniversity,
      iconClassName: 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300',
      label: 'บัญชีทั้งหมด',
      value: <span className="text-gray-800 dark:text-slate-100">{banks.length.toLocaleString()}</span>,
    },
    {
      icon: faCircleCheck,
      iconClassName: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      label: 'ใช้งาน',
      value: <span className="text-green-600 dark:text-green-400">{activeCount.toLocaleString()}</span>,
      subValue: <span className="text-xs text-gray-400 dark:text-slate-500">ปิด {banks.length - activeCount}</span>,
    },
    {
      icon: faMoneyBillTransfer,
      iconClassName: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
      label: 'บัญชีถอน',
      value: <span className="text-orange-600 dark:text-orange-400">{withdrawCount.toLocaleString()}</span>,
    },
    {
      icon: faBan,
      iconClassName: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
      label: 'ถูกอายัด',
      value: <span className="text-red-600 dark:text-red-400">{suspendedCount.toLocaleString()}</span>,
    },
  ];

  const handleFilterReset = () => {
    setFilterBank('');
    setFilterType('');
    setFilterStatus('');
  };

  const columns: Column<any>[] = [
    {
      key: 'bank',
      header: 'ธนาคาร',
      render: (bank) => {
        const bankStyle = getBankStyle(bank.bank_code);
        return (
          <div className={`flex items-center gap-3 min-w-0 ${!bank.is_active ? 'opacity-60' : ''}`}>
            <div className={`w-10 h-10 rounded-lg ${bankStyle.bg} ${bankStyle.text} flex items-center justify-center font-bold text-xs shadow-sm flex-shrink-0`}>
              {bank.bank_code}
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-gray-800 dark:text-slate-100 truncate">{bank.name}</p>
              <p className="text-xs text-gray-400 dark:text-slate-500">{bankStyle.name}</p>
            </div>
          </div>
        );
      },
    },
    {
      key: 'account',
      header: 'เลขบัญชี / PromptPay',
      render: (bank) => {
        const otp = bank.credential?.otp_mailbox;
        return (
          <div>
            <p className="font-mono text-gray-700 dark:text-slate-200 text-sm">{bank.account_number}</p>
            {bank.promptpay_id && (
              <p className="font-mono text-xs text-gray-400 dark:text-slate-500 mt-0.5">PP: {bank.promptpay_id}</p>
            )}
            {/* กล่องเมลรับ OTP (Mailkub) — โชว์ Webhook URL ให้ copy ได้จากหน้ารายการ ไม่ต้องเปิด modal แก้ไข */}
            {bank.is_withdraw && otp?.email && (
              otp.webhook_url ? (
                <div className="mt-1 flex items-center gap-1.5 min-w-0" title={otp.webhook_url}>
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 flex-shrink-0">
                    Webhook OTP
                  </span>
                  <span className="font-mono text-[11px] text-gray-500 dark:text-slate-400 truncate max-w-[220px]">{otp.webhook_url}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); copyToClipboard(otp.webhook_url); }}
                    className="text-gray-400 hover:text-blue-600 dark:text-slate-500 dark:hover:text-blue-400 flex-shrink-0"
                    title="คัดลอก Webhook URL ไปวางในแท็บ Webhook ของกล่องนี้ใน Mailkub"
                  >
                    <FontAwesomeIcon icon={faCopy} className="text-xs" />
                  </button>
                </div>
              ) : (
                <p className="mt-1 text-[10px] text-amber-600 dark:text-amber-400">
                  กล่องเมล OTP ยังไม่ได้ลงทะเบียนกับ KTB server — กดแก้ไขแล้วบันทึกอีกครั้ง
                </p>
              )
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'สถานะ',
      render: (bank) => (
        <div className="flex flex-col gap-1.5 items-start">
          <div className="flex items-center gap-2">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={bank.is_active}
                onChange={(e) => handleToggleStatus(bank.id, e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-8 h-[18px] bg-gray-200 dark:bg-slate-600 peer-focus:outline-none rounded-full peer
                              peer-checked:after:translate-x-3.5 peer-checked:after:border-white
                              after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                              after:bg-white after:border-gray-300 after:border after:rounded-full
                              after:h-[14px] after:w-[14px] after:transition-all peer-checked:bg-green-500" />
            </label>
            <StatusBadge status={bank.is_active ? 'active' : 'disabled'} statusMap={BANK_STATUS_MAP} />
          </div>
          {bank.is_withdraw && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400">
              Withdraw
            </span>
          )}
          {tiersSupported && Array.isArray(bank.allowed_tier_ids) && bank.allowed_tier_ids.length > 0 && (
            <div className="flex flex-wrap gap-1" title="เฉพาะร้านระดับเหล่านี้ที่ใช้บัญชีนี้ได้">
              {bank.allowed_tier_ids.map((tierId: string) => {
                const tier = tiersById.get(tierId);
                return (
                  <span
                    key={tierId}
                    className="px-1.5 py-0.5 rounded-md text-[10px] font-semibold bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300 inline-flex items-center gap-1"
                  >
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: tier?.color || '#9ca3af' }} />
                    {tier?.name ?? '(ไม่พบ)'}
                  </span>
                );
              })}
            </div>
          )}
          {bank.suspensions?.length > 0 && (
            <span
              className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center gap-1"
              title={bank.suspensions.map((s: any) => `${s.agency_name} - ฿${Number(s.suspended_amount).toLocaleString()}`).join('\n')}
            >
              <FontAwesomeIcon icon={faBan} />
              ถูกอายัด ({bank.suspensions.length})
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'balance',
      header: 'ยอดคงเหลือ',
      render: (bank) => (
        <div className="flex items-center gap-2">
          {bankBalances[bank.id]?.loading ? (
            <FontAwesomeIcon icon={faSpinner} className="text-indigo-500 animate-spin text-xs" />
          ) : bankBalances[bank.id]?.balance != null ? (
            <span className="font-mono font-bold text-green-600 dark:text-green-400 text-xs">
              {bankBalances[bank.id].balance!.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ฿
            </span>
          ) : bankBalances[bank.id]?.error ? (
            <span className="text-[10px] text-red-400 dark:text-red-300">{bankBalances[bank.id].error}</span>
          ) : (
            <span className="text-gray-300 dark:text-slate-600">-</span>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); fetchBankBalance(bank.id); }}
            disabled={bankBalances[bank.id]?.loading}
            className="px-1.5 py-0.5 text-[10px] bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 rounded font-medium transition-colors disabled:opacity-50"
          >
            ดึงยอด
          </button>
        </div>
      ),
    },
    {
      key: 'limit',
      header: 'Limit/วัน',
      render: (bank) => bank.is_withdraw ? (
        <span className="text-gray-300 dark:text-slate-600">-</span>
      ) : (
        <div>
          <p className="font-medium text-gray-700 dark:text-slate-200 text-xs">
            {bank.daily_transaction_limit === 0 ? 'ไม่จำกัด' : `${(bank.daily_transaction_limit ?? 10000).toLocaleString()} tx`}
          </p>
          {bank.daily_used != null && bank.daily_transaction_limit > 0 && (
            <div className="mt-1 min-w-[120px]">
              <div className="w-full bg-gray-100 dark:bg-slate-600 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full ${bank.daily_used >= bank.daily_transaction_limit ? 'bg-red-500' : 'bg-green-500'}`}
                  style={{ width: `${Math.min((bank.daily_used / bank.daily_transaction_limit) * 100, 100)}%` }}
                />
              </div>
              <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5">
                {bank.daily_used.toLocaleString()}/{bank.daily_transaction_limit.toLocaleString()}
              </p>
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'deposit_limit',
      header: 'เพดานฝาก/วัน',
      render: (bank) => bank.is_withdraw ? (
        <span className="text-gray-300 dark:text-slate-600">-</span>
      ) : (
        <div>
          <p className="font-medium text-gray-700 dark:text-slate-200 text-xs">
            {Number(bank.daily_deposit_limit) > 0 ? `฿${Number(bank.daily_deposit_limit).toLocaleString()}` : 'ไม่จำกัด'}
          </p>
          {Number(bank.daily_deposit_limit) > 0 && bank.daily_deposit_used != null && (
            <div className="mt-1 min-w-[120px]">
              <div className="w-full bg-gray-100 dark:bg-slate-600 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full ${Number(bank.daily_deposit_used) >= Number(bank.daily_deposit_limit) ? 'bg-red-500' : 'bg-green-500'}`}
                  style={{ width: `${Math.min((Number(bank.daily_deposit_used) / Number(bank.daily_deposit_limit)) * 100, 100)}%` }}
                />
              </div>
              <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5">
                ฿{Number(bank.daily_deposit_used).toLocaleString(undefined, { maximumFractionDigits: 0 })}/฿{Number(bank.daily_deposit_limit).toLocaleString()}
              </p>
            </div>
          )}
        </div>
      ),
    },
    // ⭐ เพดานช่วงเวลา (N tx / S วินาที) — แสดงเฉพาะ tenant ที่ migrate แล้ว · usage จาก window_used (Redis ZCOUNT ฝั่ง backend)
    ...(windowSupported ? [{
      key: 'window_limit',
      header: 'Limit/ช่วง',
      render: (bank: any) => bank.is_withdraw ? (
        <span className="text-gray-300 dark:text-slate-600">-</span>
      ) : (
        <div>
          <p className="font-medium text-gray-700 dark:text-slate-200 text-xs">
            {Number(bank.window_transaction_limit) > 0
              ? `${Number(bank.window_transaction_limit).toLocaleString()} tx / ${fmtWindow(bank.limit_window_seconds)}`
              : 'ไม่จำกัด'}
          </p>
          {Number(bank.window_transaction_limit) > 0 && bank.window_used != null && (
            <div className="mt-1 min-w-[120px]">
              <div className="w-full bg-gray-100 dark:bg-slate-600 rounded-full h-1.5">
                <div
                  className={`h-1.5 rounded-full ${Number(bank.window_used) >= Number(bank.window_transaction_limit) ? 'bg-red-500' : 'bg-green-500'}`}
                  style={{ width: `${Math.min((Number(bank.window_used) / Number(bank.window_transaction_limit)) * 100, 100)}%` }}
                />
              </div>
              <p className="text-[10px] text-gray-400 dark:text-slate-500 mt-0.5">
                {Number(bank.window_used).toLocaleString()}/{Number(bank.window_transaction_limit).toLocaleString()}
              </p>
            </div>
          )}
        </div>
      ),
    }] : []),
    {
      key: 'email',
      header: 'Webhook Email',
      render: (bank) => bank.email_address ? (
        <div className="flex items-center gap-1 min-w-0">
          <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 text-[10px] flex-shrink-0" />
          <span className="text-xs text-gray-600 dark:text-slate-300 truncate max-w-[160px]" title={bank.email_address}>{bank.email_address}</span>
        </div>
      ) : (
        <span className="text-xs text-gray-300 dark:text-slate-600">ไม่มี</span>
      ),
    },
    {
      key: 'merchants',
      header: 'Merchants',
      render: (bank) => bank.merchants?.length > 0 ? (
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setExpandedMerchants(prev => {
                const next = new Set(prev);
                next.has(bank.id) ? next.delete(bank.id) : next.add(bank.id);
                return next;
              });
            }}
            className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-lg text-xs font-medium transition-colors"
          >
            <span>{bank.merchants.length} merchants</span>
            <FontAwesomeIcon icon={expandedMerchants.has(bank.id) ? faChevronUp : faChevronDown} className="text-[9px]" />
          </button>
          {expandedMerchants.has(bank.id) && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setExpandedMerchants(prev => { const next = new Set(prev); next.delete(bank.id); return next; })} />
              <div className="absolute z-20 top-full left-0 mt-1 bg-white dark:bg-slate-800 rounded-xl shadow-xl border border-gray-200 dark:border-slate-600 p-3 min-w-[220px] max-w-[300px]">
                <p className="text-[10px] font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-2">Merchants ({bank.merchants.length})</p>
                <div className="flex flex-wrap gap-1.5 max-h-[200px] overflow-y-auto">
                  {bank.merchants.map((m: any) => (
                    <span key={m.id} className="px-2 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md text-[11px] font-medium">
                      {m.name}
                    </span>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <span className="text-xs text-gray-300 dark:text-slate-600">ไม่มี</span>
      ),
    },
    {
      key: 'actions',
      header: 'จัดการ',
      width: '100px',
      render: (bank) => (
        <div className="flex items-center gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); handleOpenEditModal(bank); }}
            className="px-2 py-1 rounded-md text-[11px] font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
            title="แก้ไข"
          >
            <FontAwesomeIcon icon={faEdit} className="text-[10px]" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDeleteBank(bank.id); }}
            className="px-2 py-1 rounded-md text-[11px] font-medium bg-red-50 dark:bg-red-900/30 text-red-500 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
            title="ลบ"
          >
            <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faUniversity}
        title="Banks Management"
        subtitle={`จัดการบัญชีธนาคารสำหรับรับเงิน • ${banks.length.toLocaleString()} บัญชี`}
        actions={
          <UiButton icon={faPlus} onClick={() => setShowCreateModal(true)}>
            เพิ่มธนาคาร
          </UiButton>
        }
      />

      <UiStatsGrid cards={statCards} loading={loading} cols={4} />

      <UiFilterPanel onSearch={() => {}} onReset={handleFilterReset}>
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">ธนาคาร</label>
          <select value={filterBank} onChange={(e) => setFilterBank(e.target.value)} className={FILTER_SELECT_CLASS}>
            <option value="">ธนาคารทั้งหมด</option>
            <option value="SCB">ไทยพาณิชย์ (SCB)</option>
            <option value="KBANK">กสิกรไทย (KBANK)</option>
            <option value="BBL">กรุงเทพ (BBL)</option>
            <option value="KTB">กรุงไทย (KTB)</option>
            <option value="BAY">กรุงศรี (BAY)</option>
            <option value="GSB">ออมสิน (GSB)</option>
          </select>
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">ประเภท</label>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className={FILTER_SELECT_CLASS}>
            <option value="">ประเภททั้งหมด</option>
            <option value="deposit">ฝาก</option>
            <option value="withdraw">ถอน</option>
          </select>
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">สถานะ</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={FILTER_SELECT_CLASS}>
            <option value="">สถานะทั้งหมด</option>
            <option value="active">Active</option>
            <option value="disabled">Disabled</option>
            <option value="suspended">ถูกอายัด</option>
          </select>
        </div>
      </UiFilterPanel>

      <DataTable
        data={filteredBanks}
        columns={columns}
        loading={loading}
        keyExtractor={(b) => b.id}
        emptyMessage="ยังไม่มีธนาคาร"
        emptyIcon={<FontAwesomeIcon icon={faUniversity} className="text-5xl text-gray-200 dark:text-slate-600" />}
        hoverable
        compact
        pagination={{
          enabled: true,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          showPageSizeSelector: true,
          showInfo: true,
          showFirstLast: true,
        }}
      />

      <UiModal
        open={showCreateModal}
        onClose={() => { setShowCreateModal(false); setFormData(emptyBankForm()); setCreateTierIds([]); }}
        title="เพิ่มธนาคาร"
        icon={faPlus}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="xl"
        scrollable
      >
        <form onSubmit={handleSubmit} className="space-y-5">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={LABEL_CLASS}>ชื่อบัญชี *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>ธนาคาร *</label>
                  <select
                    value={formData.bank_code}
                    onChange={(e) => setFormData({ ...formData, bank_code: e.target.value })}
                    required
                    className={INPUT_CLASS}
                  >
                    <option value="">เลือกธนาคาร</option>
                    {bankList.map(b => (
                      <option key={b.shortName} value={b.shortName}>ธนาคาร{b.bankName} ({b.shortName})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL_CLASS}>เลขบัญชี *</label>
                  <input
                    type="text"
                    value={formData.account_number}
                    onChange={(e) => setFormData({ ...formData, account_number: e.target.value })}
                    required
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>เลข PromptPay</label>
                  <input
                    type="text"
                    value={formData.promptpay_id}
                    onChange={(e) => setFormData({ ...formData, promptpay_id: e.target.value })}
                    placeholder="เบอร์โทรหรือเลขบัตรประชาชน (ไม่บังคับ)"
                    className={INPUT_CLASS}
                  />
                </div>
                {!formData.is_withdraw && (
                  <BankLimitSection
                    values={formData}
                    onChange={(patch) => setFormData(prev => ({ ...prev, ...patch }))}
                    windowSupported={windowSupported}
                  />
                )}
              </div>

              {/* Webhook Email - ใช้หา bank จาก webhook */}
              <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 space-y-3 border border-green-100 dark:border-green-800">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                  <FontAwesomeIcon icon={faEnvelope} />
                  <h4 className="font-semibold text-sm">Email สำหรับ Webhook</h4>
                </div>
                <p className="text-xs text-green-600 dark:text-green-400">
                  ระบบจะใช้ email นี้ในการระบุธนาคารเมื่อได้รับ transaction จาก webhook
                </p>
                <div>
                  <label className="block text-xs font-medium text-green-700 dark:text-green-300 mb-1">Email Address *</label>
                  <input
                    type="email"
                    value={formData.email_address}
                    onChange={(e) => setFormData({ ...formData, email_address: e.target.value })}
                    required
                    placeholder="email ที่ลงทะเบียนกับ SCB Business Alert"
                    className={INPUT_CLASS}
                  />
                </div>
              </div>

              {/* Withdraw Settings */}
              {(formData.bank_code === 'KTB' || formData.bank_code === 'SCB') && (
              <>
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-4 space-y-3 border border-orange-100 dark:border-orange-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-orange-700 dark:text-orange-300 text-sm">เปิดใช้งานสำหรับถอนเงิน</h4>
                      {formData.bank_code === 'KTB' && (
                        <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">เปิดใช้งานเพื่อให้ธนาคารนี้สามารถใช้สำหรับถอนเงินอัตโนมัติได้</p>
                      )}
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_withdraw}
                        onChange={(e) => setFormData({ ...formData, is_withdraw: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500" />
                    </label>
                  </div>
                </div>
                {formData.is_withdraw && formData.bank_code === 'KTB' && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 space-y-3 border border-blue-100 dark:border-blue-800">
                    <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                      <FontAwesomeIcon icon={faUniversity} />
                      <h4 className="font-semibold text-sm">ข้อมูล Credential สำหรับถอนเงิน (KTB Business)</h4>
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">Company ID</label>
                        <input type="text" value={formData.credential.companyId}
                          onChange={(e) => setFormData({ ...formData, credential: { ...formData.credential, companyId: e.target.value } })}
                          placeholder="เช่น SSKBA11221" className={INPUT_CLASS} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">User ID</label>
                        <input type="text" value={formData.credential.userId}
                          onChange={(e) => setFormData({ ...formData, credential: { ...formData.credential, userId: e.target.value } })}
                          placeholder="เช่น USEREIU21ktb" className={INPUT_CLASS} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">Password</label>
                        <input type="password" value={formData.credential.password}
                          onChange={(e) => setFormData({ ...formData, credential: { ...formData.credential, password: e.target.value } })}
                          placeholder="รหัสผ่าน" className={INPUT_CLASS} />
                      </div>
                    </div>

                    {/* กล่องเมลรับ OTP (Mailkub) */}
                    <div className="pt-3 border-t border-blue-100 dark:border-blue-800 space-y-3">
                      <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                        <FontAwesomeIcon icon={faEnvelope} />
                        <h4 className="font-semibold text-sm">กล่องเมลรับ OTP (Mailkub)</h4>
                      </div>
                      <p className="text-xs text-blue-600/80 dark:text-blue-300/80">
                        ค่า API key และ webhook secret คัดลอกจากแท็บ Keys / Webhook ของกล่องเมลนั้นใน Mailkub
                      </p>
                      <div className="grid grid-cols-1 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">อีเมลกล่อง Mailkub</label>
                          <input type="text" value={formData.credential.otp_mailbox.email}
                            onChange={(e) => setFormData({ ...formData, credential: { ...formData.credential, otp_mailbox: { ...formData.credential.otp_mailbox, email: e.target.value } } })}
                            placeholder="เช่น otp-ktb@yourdomain.com" className={INPUT_CLASS} />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">Mailkub API key</label>
                          <input type="password" value={formData.credential.otp_mailbox.api_key}
                            onChange={(e) => setFormData({ ...formData, credential: { ...formData.credential, otp_mailbox: { ...formData.credential.otp_mailbox, api_key: e.target.value } } })}
                            placeholder="mk_live_…" className={INPUT_CLASS} autoComplete="new-password" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">Mailkub webhook secret</label>
                          <input type="password" value={formData.credential.otp_mailbox.webhook_secret}
                            onChange={(e) => setFormData({ ...formData, credential: { ...formData.credential, otp_mailbox: { ...formData.credential.otp_mailbox, webhook_secret: e.target.value } } })}
                            placeholder="whsec_…" className={INPUT_CLASS} autoComplete="new-password" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {formData.is_withdraw && formData.bank_code === 'KTB' && (
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4 space-y-3 border border-emerald-100 dark:border-emerald-800">
                    <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
                      <FontAwesomeIcon icon={faMoneyBillTransfer} />
                      <h4 className="font-semibold text-sm">ข้อมูล Header สำหรับ Export ไฟล์ KTB Bulk</h4>
                    </div>
                    <p className="text-xs text-emerald-600/80 dark:text-emerald-400/80">ใช้เฉพาะตอน Export ไฟล์ Bulk Transfer — กรอกไม่บังคับ เว้นว่างได้ถ้ายังไม่ใช้</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {KTB_BULK_FIELDS.map(f => (
                        <div key={f.key}>
                          <label className="block text-xs font-medium text-emerald-700 dark:text-emerald-300 mb-1">{f.label}</label>
                          <input type="text" value={formData.bulk_detail[f.key] || ''}
                            onChange={(e) => setFormData({ ...formData, bulk_detail: { ...formData.bulk_detail, [f.key]: e.target.value } })}
                            placeholder={f.placeholder || ''} className={INPUT_CLASS} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
              )}


              {/* Merchant Selection */}
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 border border-gray-100 dark:border-slate-600">
                <h4 className="font-semibold text-gray-700 dark:text-slate-200 text-sm mb-3">เลือก Merchants ที่สามารถใช้ธนาคารนี้</h4>
                <label className="flex items-center gap-2 cursor-pointer mb-3 p-2 bg-white dark:bg-slate-700 rounded-lg border border-gray-200 dark:border-slate-600">
                  <input
                    type="checkbox"
                    checked={formData.all_merchants}
                    onChange={(e) => setFormData({ ...formData, all_merchants: e.target.checked, merchant_ids: [] })}
                    className="w-4 h-4 rounded focus:ring-gray-300 dark:focus:ring-slate-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-slate-200">ทุก Merchants (ปัจจุบันและอนาคต)</span>
                </label>
                {!formData.all_merchants && (
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                    {merchants.map((merchant) => (
                      <label
                        key={merchant.id}
                        className="flex items-center gap-2 cursor-pointer p-2 bg-white dark:bg-slate-700 rounded-lg border border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500"
                      >
                        <input
                          type="checkbox"
                          checked={formData.merchant_ids.includes(merchant.id)}
                          onChange={() => handleMerchantToggle(merchant.id)}
                          className="w-4 h-4 rounded focus:ring-gray-300 dark:focus:ring-slate-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-slate-200 truncate">{merchant.name}</span>
                      </label>
                    ))}
                  </div>
                )}
                {!formData.all_merchants && (
                  <p className="text-xs text-gray-400 dark:text-slate-500 mt-3">ไม่เลือก = ไม่มีร้านใช้ได้</p>
                )}
              </div>

              {/* ⭐ ระดับร้านค้า — ซ่อนเมื่อเป็นบัญชีถอน (คง state ไว้) / ซ่อนทั้ง block บน tenant ที่ไม่รองรับ */}
              {tiersSupported && !formData.is_withdraw && (
                <TierSelector tiers={tiers} selected={createTierIds} onToggle={toggleCreateTier} />
              )}

              <UiButton type="submit" className="w-full justify-center">
                สร้างธนาคาร
              </UiButton>
        </form>
      </UiModal>

      <UiModal
        open={showEditModal}
        onClose={() => { setShowEditModal(false); setEditingBank(null); }}
        title={`แก้ไขธนาคาร${editingBank ? ` — ${editingBank.name}` : ''}`}
        icon={faEdit}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="xl"
        scrollable
      >
        <form onSubmit={handleEditSubmit} className="space-y-5">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={LABEL_CLASS}>ชื่อบัญชี *</label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    required
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>ธนาคาร *</label>
                  <select
                    value={editFormData.bank_code}
                    onChange={(e) => setEditFormData({ ...editFormData, bank_code: e.target.value })}
                    required
                    className={INPUT_CLASS}
                  >
                    <option value="">เลือกธนาคาร</option>
                    {bankList.map(b => (
                      <option key={b.shortName} value={b.shortName}>ธนาคาร{b.bankName} ({b.shortName})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL_CLASS}>เลขบัญชี *</label>
                  <input
                    type="text"
                    value={editFormData.account_number}
                    onChange={(e) => setEditFormData({ ...editFormData, account_number: e.target.value })}
                    required
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>เลข PromptPay</label>
                  <input
                    type="text"
                    value={editFormData.promptpay_id}
                    onChange={(e) => setEditFormData({ ...editFormData, promptpay_id: e.target.value })}
                    placeholder="เบอร์โทรหรือเลขบัตรประชาชน (ไม่บังคับ)"
                    className={INPUT_CLASS}
                  />
                </div>
                {!editFormData.is_withdraw && (
                  <BankLimitSection
                    values={editFormData}
                    onChange={(patch) => setEditFormData(prev => ({ ...prev, ...patch }))}
                    windowSupported={windowSupported}
                  />
                )}
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 space-y-3 border border-green-100 dark:border-green-800">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                  <FontAwesomeIcon icon={faEnvelope} />
                  <h4 className="font-semibold text-sm">Email สำหรับ Webhook</h4>
                </div>
                <p className="text-xs text-green-600 dark:text-green-400">
                  ระบบจะใช้ email นี้ในการระบุธนาคารเมื่อได้รับ transaction จาก webhook
                </p>
                <div>
                  <label className="block text-xs font-medium text-green-700 dark:text-green-300 mb-1">Email Address *</label>
                  <input
                    type="email"
                    value={editFormData.email_address}
                    onChange={(e) => setEditFormData({ ...editFormData, email_address: e.target.value })}
                    required
                    placeholder="email ที่ลงทะเบียนกับ SCB Business Alert"
                    className={INPUT_CLASS}
                  />
                </div>
              </div>

              {(editFormData.bank_code === 'KTB' || editFormData.bank_code === 'SCB') && (
              <>
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-4 space-y-3 border border-orange-100 dark:border-orange-800">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-orange-700 dark:text-orange-300 text-sm">เปิดใช้งานสำหรับถอนเงิน</h4>
                      {editFormData.bank_code === 'KTB' && (
                        <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">เปิดใช้งานเพื่อให้ธนาคารนี้สามารถใช้สำหรับถอนเงินอัตโนมัติได้</p>
                      )}
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editFormData.is_withdraw}
                        onChange={(e) => setEditFormData({ ...editFormData, is_withdraw: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 dark:bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500" />
                    </label>
                  </div>
                </div>
                {editFormData.is_withdraw && editFormData.bank_code === 'KTB' && (
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 space-y-3 border border-blue-100 dark:border-blue-800">
                    <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                      <FontAwesomeIcon icon={faUniversity} />
                      <h4 className="font-semibold text-sm">ข้อมูล Credential สำหรับถอนเงิน (KTB Business)</h4>
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">Company ID</label>
                        <input type="text" value={editFormData.credential?.companyId || ''}
                          onChange={(e) => setEditFormData({ ...editFormData, credential: { ...editFormData.credential, companyId: e.target.value } })}
                          placeholder="เช่น SSKBA08105" className={INPUT_CLASS} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">User ID</label>
                        <input type="text" value={editFormData.credential?.userId || ''}
                          onChange={(e) => setEditFormData({ ...editFormData, credential: { ...editFormData.credential, userId: e.target.value } })}
                          placeholder="เช่น USERACC01ktb" className={INPUT_CLASS} />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">Password</label>
                        <input type="password" value={editFormData.credential?.password || ''}
                          onChange={(e) => setEditFormData({ ...editFormData, credential: { ...editFormData.credential, password: e.target.value } })}
                          placeholder="รหัสผ่าน (เว้นว่างถ้าไม่ต้องการเปลี่ยน)" className={INPUT_CLASS} />
                      </div>
                    </div>

                    {/* กล่องเมลรับ OTP (Mailkub) */}
                    <div className="pt-3 border-t border-blue-100 dark:border-blue-800 space-y-3">
                      <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                        <FontAwesomeIcon icon={faEnvelope} />
                        <h4 className="font-semibold text-sm">กล่องเมลรับ OTP (Mailkub)</h4>
                      </div>
                      <p className="text-xs text-blue-600/80 dark:text-blue-300/80">
                        ค่า API key และ webhook secret คัดลอกจากแท็บ Keys / Webhook ของกล่องเมลนั้นใน Mailkub
                      </p>
                      <div className="grid grid-cols-1 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">อีเมลกล่อง Mailkub</label>
                          <input type="text" value={editFormData.credential.otp_mailbox.email}
                            onChange={(e) => setEditFormData({ ...editFormData, credential: { ...editFormData.credential, otp_mailbox: { ...editFormData.credential.otp_mailbox, email: e.target.value } } })}
                            placeholder="เช่น otp-ktb@yourdomain.com" className={INPUT_CLASS} />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">Mailkub API key</label>
                          <input type="password" value={editFormData.credential.otp_mailbox.api_key}
                            onChange={(e) => setEditFormData({ ...editFormData, credential: { ...editFormData.credential, otp_mailbox: { ...editFormData.credential.otp_mailbox, api_key: e.target.value } } })}
                            placeholder="mk_live_… (เว้นว่างถ้าไม่ต้องการเปลี่ยน)" className={INPUT_CLASS} autoComplete="new-password" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">Mailkub webhook secret</label>
                          <input type="password" value={editFormData.credential.otp_mailbox.webhook_secret}
                            onChange={(e) => setEditFormData({ ...editFormData, credential: { ...editFormData.credential, otp_mailbox: { ...editFormData.credential.otp_mailbox, webhook_secret: e.target.value } } })}
                            placeholder="whsec_… (เว้นว่างถ้าไม่ต้องการเปลี่ยน)" className={INPUT_CLASS} autoComplete="new-password" />
                        </div>
                        {editFormData.credential.otp_mailbox.webhook_url && (
                          <div>
                            <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">Webhook URL</label>
                            <div className="flex items-stretch gap-2">
                              <div className="flex-1 min-w-0 px-3.5 py-2.5 border border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-800 dark:text-slate-200 rounded-lg text-xs font-mono break-all select-all">
                                {editFormData.credential.otp_mailbox.webhook_url}
                              </div>
                              <button
                                type="button"
                                onClick={() => copyToClipboard(editFormData.credential.otp_mailbox.webhook_url)}
                                title="คัดลอก Webhook URL"
                                className="flex-shrink-0 px-3 rounded-lg text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:hover:bg-blue-900/60 transition-colors"
                              >
                                <FontAwesomeIcon icon={faCopy} className="mr-1" />
                                คัดลอก
                              </button>
                            </div>
                            <p className="mt-1 text-xs text-blue-600/80 dark:text-blue-300/80">
                              นำไปวางใน Mailkub → Webhook tab ของกล่องนี้ แล้วกด Send test event
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {editFormData.is_withdraw && editFormData.bank_code === 'KTB' && (
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-xl p-4 space-y-3 border border-emerald-100 dark:border-emerald-800">
                    <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
                      <FontAwesomeIcon icon={faMoneyBillTransfer} />
                      <h4 className="font-semibold text-sm">ข้อมูล Header สำหรับ Export ไฟล์ KTB Bulk</h4>
                    </div>
                    <p className="text-xs text-emerald-600/80 dark:text-emerald-400/80">ใช้เฉพาะตอน Export ไฟล์ Bulk Transfer — กรอกไม่บังคับ เว้นว่างได้ถ้ายังไม่ใช้</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {KTB_BULK_FIELDS.map(f => (
                        <div key={f.key}>
                          <label className="block text-xs font-medium text-emerald-700 dark:text-emerald-300 mb-1">{f.label}</label>
                          <input type="text" value={editFormData.bulk_detail[f.key] || ''}
                            onChange={(e) => setEditFormData({ ...editFormData, bulk_detail: { ...editFormData.bulk_detail, [f.key]: e.target.value } })}
                            placeholder={f.placeholder || ''} className={INPUT_CLASS} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
              )}

              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 border border-gray-100 dark:border-slate-600">
                <h4 className="font-semibold text-gray-700 dark:text-slate-200 text-sm mb-3">เลือก Merchants ที่สามารถใช้ธนาคารนี้</h4>
                <label className="flex items-center gap-2 cursor-pointer mb-3 p-2 bg-white dark:bg-slate-700 rounded-lg border border-gray-200 dark:border-slate-600">
                  <input
                    type="checkbox"
                    checked={editFormData.all_merchants}
                    onChange={(e) => setEditFormData({ ...editFormData, all_merchants: e.target.checked, merchant_ids: [] })}
                    className="w-4 h-4 rounded focus:ring-gray-300 dark:focus:ring-slate-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-slate-200">ทุก Merchants (ปัจจุบันและอนาคต)</span>
                </label>
                {!editFormData.all_merchants && (
                  <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
                    {merchants.map((merchant) => (
                      <label
                        key={merchant.id}
                        className={`flex items-center gap-2 cursor-pointer p-2 rounded-lg border transition-colors
                          ${editFormData.merchant_ids.includes(merchant.id)
                            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700'
                            : 'bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'}`}
                      >
                        <input
                          type="checkbox"
                          checked={editFormData.merchant_ids.includes(merchant.id)}
                          onChange={() => handleEditMerchantToggle(merchant.id)}
                          className="w-4 h-4 rounded focus:ring-gray-300 dark:focus:ring-slate-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-slate-200 truncate">{merchant.name}</span>
                      </label>
                    ))}
                  </div>
                )}
                {editFormData.merchant_ids.length > 0 && !editFormData.all_merchants && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-slate-600">
                    <p className="text-xs text-gray-500 dark:text-slate-400 mb-2">Merchants ที่เลือก ({editFormData.merchant_ids.length}):</p>
                    <div className="flex flex-wrap gap-1.5">
                      {editFormData.merchant_ids.map((id) => {
                        const merchant = merchants.find(m => m.id === id);
                        return merchant ? (
                          <span key={id} className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md text-xs font-medium flex items-center gap-1">
                            {merchant.name}
                            <button type="button" onClick={() => handleEditMerchantToggle(id)} className="text-blue-400 hover:text-blue-600 dark:hover:text-blue-200">
                              <FontAwesomeIcon icon={faTimes} className="text-[10px]" />
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
                {!editFormData.all_merchants && (
                  <p className="text-xs text-gray-400 dark:text-slate-500 mt-3">ไม่เลือก = ไม่มีร้านใช้ได้</p>
                )}
              </div>

              {tiersSupported && !editFormData.is_withdraw && (
                <>
                  <TierSelector tiers={tiers} selected={editTierIds} onToggle={toggleEditTier} />
                  {depositAdminBankId !== '' && editingBank?.id === depositAdminBankId && editTierIds.length > 0 && (
                    <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                      <span className="text-amber-500 text-base leading-none">⚠️</span>
                      <p className="text-xs text-amber-800 dark:text-amber-200">
                        บัญชีนี้เป็น <b>บัญชี fallback ของ P2P</b> (deposit_admin_bank_id) — เมื่อจำกัดระดับ ร้าน P2P ที่ระดับไม่ตรงและไม่มีบัญชีผูกของตัวเองจะฝากไม่ได้ (503 NO_ADMIN_BANK) ทุกใบที่จับคู่ payout ไม่ได้
                      </p>
                    </div>
                  )}
                </>
              )}

              <UiButton type="submit" icon={faEdit} className="w-full justify-center">
                บันทึกการแก้ไข
              </UiButton>
        </form>
      </UiModal>
    </div>
  );
};

export default BanksManagementPage;
